import { Stack, Text, ThemeIcon, Title, Box } from "@mantine/core"
import { useHover } from "@mantine/hooks"
import { IconListSearch } from "@tabler/icons-react"
import { FC, useEffect, useState } from "react"

import {
  FoldersResponses,
  TreeNodeType,
} from "@costory/types/endpoints/folders"
import { ComponentType } from "@costory/types/prisma-client"

import {
  FileListItem,
  FileListItemProps,
  ROW_HEIGHT,
} from "@costory/front/components/FileExplorer/FileListItem"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { useFoldersSearch } from "@costory/front/queries/folders"
import { PropsWithData } from "@costory/front/utils/propsWithData"

interface ItemRowProps extends Omit<FileListItemProps, "onClick"> {
  selected: boolean
  keyboardFocused?: boolean
  onDoubleClick?: (
    id: string,
    type: TreeNodeType,
    event?: React.MouseEvent,
  ) => void
  onSelect: (
    id: string,
    label: string,
    type: TreeNodeType,
    closestFolderId: string,
    ownerId: string,
    componentType?: ComponentType,
  ) => void
}

const ItemRow = ({
  selected,
  keyboardFocused,
  onSelect,
  onDoubleClick,
  ...leafRowProps
}: ItemRowProps) => {
  const { ref: hoverRef, hovered } = useHover()
  const [itemNode, setItemNode] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    if (keyboardFocused && itemNode) {
      itemNode.scrollIntoView({ block: "nearest", behavior: "smooth" })
    }
  }, [keyboardFocused, itemNode])

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (e.metaKey || e.ctrlKey) {
      onDoubleClick?.(leafRowProps.id, leafRowProps.type, e)
      return
    }
    onSelect(
      leafRowProps.id,
      leafRowProps.label,
      leafRowProps.type,
      leafRowProps.type === TreeNodeType.Folder
        ? leafRowProps.id
        : leafRowProps.parentFolderId,
      leafRowProps.ownerId,
      leafRowProps.componentType as ComponentType,
    )
  }
  const handleNodeDoubleClick = () => {
    onDoubleClick?.(leafRowProps.id, leafRowProps.type)
  }

  const mergedRef = (node: HTMLDivElement | null) => {
    setItemNode(node)
    hoverRef(node)
  }

  return (
    <Box
      w="100%"
      h={ROW_HEIGHT}
      display="flex"
      ref={mergedRef}
      mb={4}
      pl={30}
      pr={30}
      onClick={handleClick}
      onDoubleClick={handleNodeDoubleClick}
      bg={
        selected || keyboardFocused
          ? "surfaceSelected"
          : hovered
            ? "surfaceHover"
            : "transparent"
      }
      style={{ cursor: "default", borderRadius: 8, alignItems: "center" }}
    >
      <FileListItem
        {...leafRowProps}
        onClick={(e: React.MouseEvent<HTMLDivElement>) =>
          onDoubleClick?.(leafRowProps.id, leafRowProps.type, e) &&
          handleNodeDoubleClick
        }
        disableHighlightStyle={leafRowProps.disableHighlightStyle}
      />
    </Box>
  )
}

interface _SearchExplorerProps {
  onSelect: (
    id: string,
    label: string,
    type: TreeNodeType,
    closestFolderId: string,
    ownerId: string,
    componentType?: ComponentType,
  ) => void
  shouldShowDashboards: boolean
  shouldShowSavedViews: boolean
  disableHighlightStyle?: boolean
  keyboardFocusIndex?: number
  onDoubleClick?: (
    id: string,
    type: TreeNodeType,
    event?: React.MouseEvent,
  ) => void
}

const _SearchExplorer: FC<
  _SearchExplorerProps & PropsWithData<FoldersResponses.Search>
> = ({
  data,
  onSelect,
  shouldShowDashboards,
  shouldShowSavedViews,
  onDoubleClick,
  disableHighlightStyle,
  keyboardFocusIndex = -1,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const handleSelect = (
    itemId: string,
    label: string,
    type: TreeNodeType,
    closestFolderId: string,
    ownerId: string,
    componentType?: ComponentType,
  ) => {
    setSelectedId((prev) => (itemId === prev ? null : itemId))
    onSelect(itemId, label, type, closestFolderId, ownerId, componentType)
  }

  const filteredData = data
    .filter(
      (item) => shouldShowDashboards || item.type !== TreeNodeType.Dashboard,
    )
    .filter(
      (item) => shouldShowSavedViews || item.type !== TreeNodeType.SavedView,
    )

  // Auto-select the keyboard-focused item
  useEffect(() => {
    if (keyboardFocusIndex >= 0 && keyboardFocusIndex < filteredData.length) {
      const item = filteredData[keyboardFocusIndex]
      handleSelect(
        item.id,
        item.label,
        item.type,
        item.type === TreeNodeType.Folder ? item.id : item.parentFolderId,
        item.ownerId,
        item.componentType as ComponentType,
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyboardFocusIndex])

  return (
    <Stack p={8} gap={2}>
      {filteredData.length === 0 ? (
        <Stack align="center" py={80}>
          <ThemeIcon size={70}>
            <IconListSearch size={70} />
          </ThemeIcon>
          <Title>We found no file matching your request</Title>
          <Text fz="xl">Have you tried other keywords ?</Text>
        </Stack>
      ) : (
        filteredData.map((props, index) => (
          <ItemRow
            key={props.id}
            {...props}
            onSelect={handleSelect}
            selected={props.id === selectedId}
            keyboardFocused={index === keyboardFocusIndex}
            onDoubleClick={onDoubleClick}
            disableHighlightStyle={disableHighlightStyle}
          />
        ))
      )}
    </Stack>
  )
}

interface SearchExplorerProps extends _SearchExplorerProps {
  search: string
  disableHighlightStyle?: boolean
  keyboardFocusIndex?: number
}

export const SearchExplorer = ({
  search,
  disableHighlightStyle,
  keyboardFocusIndex,
  ..._foldersSearchProps
}: SearchExplorerProps) => {
  const query = useFoldersSearch(search)
  return (
    <QueryWrapper query={query} allowEmptyArray>
      {({ data }) => (
        <_SearchExplorer
          data={data}
          {..._foldersSearchProps}
          disableHighlightStyle={disableHighlightStyle}
          keyboardFocusIndex={keyboardFocusIndex}
        />
      )}
    </QueryWrapper>
  )
}
